/**
 * EmailAutosave — manages draft autosave for the compose form.
 *
 * Usage:
 *   const autosave = new EmailAutosave({ store, getPayload, onSaved, onCreated })
 *   autosave.start()
 *   autosave.stop()
 *   autosave.saveNow()     // manual trigger
 *   autosave.notifyChange() // call on every keystroke for debounced save
 *   autosave.deleteDraft() // deletes the saved draft from server
 *
 * getPayload must return an object like:
 *   { email_draft: { mailbox_id, subject, body, to_addresses, cc_addresses, draft_type, reply_to_email_id } }
 * or null/undefined to skip the save.
 */

import { post, patch, destroy as railsDestroy } from '@rails/request.js'

const DEFAULT_INTERVAL_MS = 8000   // periodic poll interval
const DEBOUNCE_MS         = 2000   // save this many ms after last keystroke

export class EmailAutosave {
  constructor({ store, getPayload, onSaved, onCreated, intervalMs = DEFAULT_INTERVAL_MS }) {
    this._store            = store
    this._getPayload       = getPayload        // () => { email_draft: {...} } | null
    this._onSaved          = onSaved           // () => void
    this._onCreated        = onCreated         // (id) => void
    this._intervalMs       = intervalMs
    this._timer            = null
    this._debounceTimer    = null
    this._lastBody         = null
    this._saving           = false
  }

  start() {
    if (this._timer) return
    this._timer = setInterval(() => this.saveNow(), this._intervalMs)
  }

  stop() {
    if (this._timer) clearInterval(this._timer)
    this._timer = null
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
    this._debounceTimer = null
  }

  // Call this on every input event for debounced save
  notifyChange() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => {
      this._debounceTimer = null
      this.saveNow()
    }, DEBOUNCE_MS)
  }

  async saveNow() {
    if (this._saving) return
    const payload = this._getPayload?.()
    if (!payload) return

    const body = payload.email_draft?.body
    if (body === this._lastBody) return   // skip if unchanged
    if (!body && !this._store.draftId.value) return  // don't create empty draft

    this._lastBody = body
    this._saving = true

    try {
      const draftId = this._store.draftId.value

      if (draftId) {
        await patch(`/emails/drafts/${draftId}`, {
          body: JSON.stringify(payload),
          contentType: 'application/json',
          responseKind: 'json'
        })
        this._store.markComposeClean()
        this._onSaved?.()
      } else {
        const res = await post('/emails/drafts', {
          body: JSON.stringify(payload),
          contentType: 'application/json',
          responseKind: 'json'
        })
        if (res.ok) {
          const data = await res.json
          if (data?.id) {
            this._store.setDraftId(data.id)
            this._onCreated?.(data.id)
            // Instant local drafts badge +1 (no ActionCable round-trip)
            this._store.adjustSidebarCount('drafts', +1)
          }
          this._store.markComposeClean()
          this._onSaved?.()
        }
      }
    } catch (_) {
      // Silent — autosave failures should not interrupt the user
    } finally {
      this._saving = false
    }
  }

  async deleteDraft() {
    this.stop()
    const draftId = this._store.draftId.value
    if (!draftId) return
    try {
      await railsDestroy(`/emails/drafts/${draftId}`, { responseKind: 'json' })
      this._store.setDraftId(null)
      // Instant local drafts badge -1 (no ActionCable round-trip)
      this._store.adjustSidebarCount('drafts', -1)
    } catch (_) {
      // Silent
    }
  }

  reset() {
    this._lastBody = null
  }
}
